import { createSlice, createSelector, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from './types';
import type { IconStyleVariant } from '../theme/utils';

export type TabletPortraitNavLayout = 'native' | 'sidebar';

export type PrinterMode =
  | 'bluetooth'
  | 'tcp'
  | 'usb'
  | 'network'
  | 'web-bluetooth'
  | 'web-serial'
  | 'serial'
  | 'spooler';

export interface PrinterProfile {
  id: string;
  label: string;
  enabled: boolean;
  mode: PrinterMode;
  // BLE (native)
  bluetoothAddress?: string;
  // TCP (native)
  ip?: string;
  port?: number;
  timeoutMs?: number;
  // USB (native Android + WebUSB)
  usbVendorId?: string;
  usbProductId?: string;
  usbSerialNumber?: string;
  usbProductName?: string;
  // QZ Tray (web/desktop)
  printerName?: string;
  // Web Bluetooth (GATT)
  bleDeviceId?: string;
  bleDeviceName?: string;
  bleServiceUuid?: string;
  bleCharacteristicUuid?: string;
  bleMtu?: number;
  bleNamePrefix?: string;
  // Web Serial (browser navigator.serial)
  serialBaudRate?: number;
  serialDataBits?: 7 | 8;
  serialStopBits?: 1 | 2;
  serialParity?: 'none' | 'even' | 'odd';
  serialFlowControl?: 'none' | 'hardware';
  serialBufferSize?: number;
  serialUsbVendorId?: string;
  serialUsbProductId?: string;
  // Electron serial (main process via serialport)
  serialPath?: string;
  // Paper format (per-printer)
  autoCut: boolean;
  widthMM: number;
  dpi: number;
  charsPerLine: number;
}

export type PrinterProfileStatusKind =
  | 'unknown'
  | 'checking'
  | 'available'
  | 'not_detected'
  | 'failed';

export type PrinterProfileStatusSource = 'discovery' | 'test' | 'print';

export interface PrinterProfileStatus {
  kind: PrinterProfileStatusKind;
  message: string;
  updatedAt: number;
  source: PrinterProfileStatusSource;
  lastSuccessAt?: number;
  lastFailureAt?: number;
}

export type PrinterProfileStatusMap = Record<string, PrinterProfileStatus>;

/** Migrate legacy addProducts/addExpenses to unified add key */
function migrateQuickActionsToAdd(
  order: string[],
  enabled: string[],
): { order: string[]; enabled: string[] } {
  const needsMigration =
    order.includes('addProducts') ||
    order.includes('addExpenses') ||
    enabled.includes('addProducts') ||
    enabled.includes('addExpenses');
  if (!needsMigration) return { order, enabled };

  const newOrder = order
    .map(k => (k === 'addProducts' || k === 'addExpenses' ? 'add' : k))
    .filter((k, i, arr) => k !== 'add' || arr.indexOf('add') === i);

  const newEnabled = enabled.filter(
    k => k !== 'addProducts' && k !== 'addExpenses',
  );
  if (
    (enabled.includes('addProducts') || enabled.includes('addExpenses')) &&
    !newEnabled.includes('add')
  ) {
    newEnabled.push('add');
  }

  return { order: newOrder, enabled: newEnabled };
}

/** Insert create quick action after add for existing persisted settings */
function migrateQuickActionsCreate(
  order: string[],
  enabled: string[],
): { order: string[]; enabled: string[] } {
  if (order.includes('create')) {
    return { order, enabled };
  }
  const addIndex = order.indexOf('add');
  const newOrder =
    addIndex >= 0
      ? [
          ...order.slice(0, addIndex + 1),
          'create',
          ...order.slice(addIndex + 1),
        ]
      : ['create', ...order];
  let newEnabled = [...enabled];
  if (!newEnabled.includes('create') && newEnabled.includes('add')) {
    newEnabled = [...newEnabled, 'create'];
  }
  return { order: newOrder, enabled: newEnabled };
}

export interface SettingsSliceObject {
  settings: {
    allow_partial_orders: boolean;
    allow_staff_order_assignment: boolean;
    allow_push_notification: boolean;
    showBizplusVisionFirstPopup: boolean;
    allow_sms_notification: boolean;
    allow_email_notification: boolean;
    showAddFromWebPopup: boolean;
    disable_tooltips: boolean;
    // UI
    showProductImages: boolean;
    /**
     * Alphabetical (A–Z) side rail in the native product list.
     * Beta: enabled by default to match current shipped behavior; user can
     * opt out from Product Settings. A missing/undefined value is treated as
     * ON by the selector (see `selectShowAlphaRail`).
     */
    showAlphaRail?: boolean;
    /**
     * Icon style variant for action icons (home screen, quick tools, etc.)
     * - 'filled': White icon, solid background color
     * - 'tinted': Solid icon color, light background, subtle border
     */
    iconStyle: IconStyleVariant;
    /**
     * Quick action color palette on Home.
     * - 'monochrome': neutral gray tiles
     * - 'colored': per-action brand colors (default)
     */
    quickActionColorMode?: 'monochrome' | 'colored';
    tabletPortraitNavLayout?: TabletPortraitNavLayout;
    // Product card UI (scalable container for future options)
    productCard?: {
      layout: 'row' | 'grid';
      quantityButtons?: {
        // main = solid icon color; background automatically derived as <main>Light
        plus: { main: string };
        minus: { main: string };
      };
      quantityInteraction?: 'buttons' | 'gesture';
      // future: grid: { columns: number }, row: { density: 'comfortable' | 'compact' }
    };
    // Scanner
    defaultScannerMode?: 'barcode' | 'ocr';
    scannerModePreference?: 'barcode_first' | 'ocr_first';
    // Scanner intro (per mode)
    showScannerIntroBarcode: boolean;
    showScannerIntroOcr: boolean;
    // General
    showLastProductRemovalPopup: boolean;
    showIncompleteOrderPopup: boolean;
    // First-run educational popups
    showServiceDisplayFirstPopup: boolean;
    /**
     * Security settings (persisted).
     * - backofficeAccessMode:
     *   - 'autologin_only': current behavior (no biometric gate)
     *   - 'biometrics_gate': auto-login still runs, but Back Office access is gated by biometrics
     */
    security: {
      backofficeAccessMode: 'autologin_only' | 'biometrics_gate';
      /**
       * Employees: when enabled, require authentication whenever app returns to foreground.
       */
      employeeRequireBiometricsOnForeground: boolean;
      /**
       * Back Office biometric gate TTL in ms. Must reset on background (runtime-only reset).
       */
      backofficeUnlockTtlMs: number;
      /**
       * When false, do NOT allow device-credential fallback (PIN/pattern/password).
       * This matches "biometrics only" requirement.
       */
      allowDeviceCredentialsFallback: boolean;
    };
    // Tables
    enableTableRotation?: boolean;
    /**
     * When true (default), show the Back Office floating "Quick Sell" button
     * that jumps to the employee POS product list. Back Office only.
     */
    enableQuickSell?: boolean;
    // Printer
    printer: {
      mode: PrinterMode;
      /**
       * Legacy single-printer BLE selection (MAC).
       * Keep for backward compatibility and as a "primary" target when multi-select is not used.
       */
      bluetoothAddress?: string;
      /** Multi-select BLE targets (MACs). When set/non-empty, print to all sequentially. */
      bluetoothAddresses?: string[];
      ip: string;
      port: number;
      /** Android USB thermal (vendor/product from USB device list) */
      usbVendorId?: string;
      usbProductId?: string;
      /** QZ Tray (web): OS-level printer name discovered via QZ */
      printerName?: string;
      /** Web Bluetooth (GATT) */
      bleDeviceId?: string;
      bleDeviceName?: string;
      bleServiceUuid?: string;
      bleCharacteristicUuid?: string;
      bleMtu?: number;
      bleNamePrefix?: string;
      /** Web Serial (browser navigator.serial) */
      serialBaudRate?: number;
      serialDataBits?: 7 | 8;
      serialStopBits?: 1 | 2;
      serialParity?: 'none' | 'even' | 'odd';
      serialFlowControl?: 'none' | 'hardware';
      serialBufferSize?: number;
      serialUsbVendorId?: string;
      serialUsbProductId?: string;
      /** Electron serial (main process via serialport) */
      serialPath?: string;
      autoCut: boolean;
      timeoutMs: number;
      widthMM: number;
      dpi: number;
      charsPerLine: number;
    };
    quickActionOrder: string[];
    enabledQuickActions: string[];
    // Flags
    serviceDisplayManualOverride?: boolean;
    /**
     * Service Display list order by `service.number` (numeric).
     * - `desc`: latest / highest number first (default)
     * - `asc`: oldest / lowest number first
     */
    serviceDisplaySortOrder?: 'desc' | 'asc';
    /** When false, OwnerTodayStatsCard shows asterisks instead of actual values */
    showTodayStatsValues?: boolean;
    // Multi-printer profiles
    printerProfiles?: PrinterProfile[];
    printerProfileStatuses?: PrinterProfileStatusMap;
    printerProfilesMigrated?: boolean;
    quickActionsCollapsed?: boolean;
  };
}

// Stable defaults to avoid returning new object references from selectors
const DEFAULT_QUANTITY_BUTTONS = Object.freeze({
  plus: { main: 'success' },
  minus: { main: 'error' },
});

const DEFAULT_PRODUCT_CARD = Object.freeze({
  layout: 'grid' as const,
  quantityButtons: DEFAULT_QUANTITY_BUTTONS,
  quantityInteraction: 'buttons' as const,
});

const initialState: SettingsSliceObject = {
  settings: {
    allow_partial_orders: false,
    allow_staff_order_assignment: false,
    allow_push_notification: false,
    allow_sms_notification: false,
    allow_email_notification: false,
    showBizplusVisionFirstPopup: true,
    showAddFromWebPopup: true,
    disable_tooltips: true,
    showProductImages: true,
    showAlphaRail: true,
    iconStyle: 'tinted',
    quickActionColorMode: 'colored',
    tabletPortraitNavLayout: 'native',
    defaultScannerMode: 'barcode',
    scannerModePreference: 'barcode_first',
    showScannerIntroBarcode: true,
    showScannerIntroOcr: true,
    showLastProductRemovalPopup: true,
    showIncompleteOrderPopup: true,
    showServiceDisplayFirstPopup: true,
    security: {
      backofficeAccessMode: 'autologin_only',
      employeeRequireBiometricsOnForeground: false,
      backofficeUnlockTtlMs: 2 * 60 * 60 * 1000,
      allowDeviceCredentialsFallback: true,
    },
    enableTableRotation: true,
    enableQuickSell: true,
    productCard: {
      layout: 'row',
      quantityButtons: {
        plus: { main: 'success' },
        minus: { main: 'error' },
      },
      quantityInteraction: 'buttons',
    },
    printer: {
      mode: 'bluetooth',
      bluetoothAddress: undefined,
      bluetoothAddresses: undefined,
      ip: '192.168.100.246',
      port: 9100,
      usbVendorId: undefined,
      usbProductId: undefined,
      printerName: undefined,
      bleDeviceId: undefined,
      bleDeviceName: undefined,
      bleServiceUuid: undefined,
      bleCharacteristicUuid: undefined,
      bleMtu: undefined,
      bleNamePrefix: undefined,
      serialBaudRate: undefined,
      serialDataBits: undefined,
      serialStopBits: undefined,
      serialParity: undefined,
      serialFlowControl: undefined,
      serialBufferSize: undefined,
      serialUsbVendorId: undefined,
      serialUsbProductId: undefined,
      serialPath: undefined,
      autoCut: false,
      timeoutMs: 30000,
      widthMM: 48,
      dpi: 203,
      charsPerLine: 32,
    },
    quickActionOrder: [
      'add',
      'create',
      'scanner',
      'calculator',
      'serviceDisplay',
      'notes',
      'aiAssistant',
    ],
    enabledQuickActions: [
      'add',
      'create',
      'scanner',
      'notes',
      'calculator',
      'aiAssistant',
    ],
    serviceDisplayManualOverride: false,
    serviceDisplaySortOrder: 'desc',
    showTodayStatsValues: true,
    printerProfiles: [],
    printerProfileStatuses: {},
    printerProfilesMigrated: false,
  },
};

function generateUid(): string {
  return Date.now().toString(36) + Math.random().toString(36).slice(2);
}

type SettingsPayload = Partial<SettingsSliceObject['settings']>;

export const settingsSlice = createSlice({
  name: 'settings',
  initialState,
  reducers: {
    addSettings: (state, action: PayloadAction<SettingsPayload>) => {
      state.settings = { ...state.settings, ...action.payload };
    },
    addSingleSetting: (state, action: PayloadAction<SettingsPayload>) => {
      const payload = action.payload;
      if (
        payload &&
        ('quickActionOrder' in payload || 'enabledQuickActions' in payload)
      ) {
        const order =
          payload.quickActionOrder ?? state.settings.quickActionOrder;
        const enabled =
          payload.enabledQuickActions ?? state.settings.enabledQuickActions;
        const migratedAdd = migrateQuickActionsToAdd(order, enabled);
        const { order: newOrder, enabled: newEnabled } =
          migrateQuickActionsCreate(migratedAdd.order, migratedAdd.enabled);
        state.settings = {
          ...state.settings,
          ...payload,
          quickActionOrder: newOrder,
          enabledQuickActions: newEnabled,
        };
      } else {
        state.settings = { ...state.settings, ...payload };
      }
    },
    updateSecuritySettings: (
      state,
      action: PayloadAction<
        Partial<SettingsSliceObject['settings']['security']>
      >,
    ) => {
      state.settings.security = {
        ...state.settings.security,
        ...action.payload,
      };
    },
    addPrinterProfiles: (state, action: PayloadAction<PrinterProfile[]>) => {
      const profiles = state.settings.printerProfiles ?? [];
      state.settings.printerProfiles = [...profiles, ...action.payload];
    },
    updatePrinterProfile: (
      state,
      action: PayloadAction<{ id: string; changes: Partial<PrinterProfile> }>,
    ) => {
      const profiles = state.settings.printerProfiles ?? [];
      const idx = profiles.findIndex(p => p.id === action.payload.id);
      if (idx >= 0) {
        profiles[idx] = { ...profiles[idx], ...action.payload.changes };
      }
    },
    deletePrinterProfile: (state, action: PayloadAction<{ id: string }>) => {
      state.settings.printerProfiles = (
        state.settings.printerProfiles ?? []
      ).filter(p => p.id !== action.payload.id);
      if (state.settings.printerProfileStatuses?.[action.payload.id]) {
        delete state.settings.printerProfileStatuses[action.payload.id];
      }
    },
    togglePrinterProfile: (state, action: PayloadAction<{ id: string }>) => {
      const profiles = state.settings.printerProfiles ?? [];
      const profile = profiles.find(p => p.id === action.payload.id);
      if (profile) {
        profile.enabled = !profile.enabled;
      }
    },
    reorderPrinterProfiles: (
      state,
      action: PayloadAction<{ ids: string[] }>,
    ) => {
      const profiles = state.settings.printerProfiles ?? [];
      const byId = new Map(profiles.map(p => [p.id, p]));
      const reordered: PrinterProfile[] = [];
      for (const id of action.payload.ids) {
        const p = byId.get(id);
        if (p) reordered.push(p);
      }
      state.settings.printerProfiles = reordered;
    },
    setPrinterProfileStatus: (
      state,
      action: PayloadAction<{
        id: string;
        status: Omit<PrinterProfileStatus, 'updatedAt'> & {
          updatedAt?: number;
        };
      }>,
    ) => {
      const statuses = state.settings.printerProfileStatuses ?? {};
      const existing = statuses[action.payload.id];
      state.settings.printerProfileStatuses = {
        ...statuses,
        [action.payload.id]: {
          ...existing,
          ...action.payload.status,
          updatedAt: action.payload.status.updatedAt ?? Date.now(),
        },
      };
    },
    clearPrinterProfileStatus: (
      state,
      action: PayloadAction<{ id: string }>,
    ) => {
      if (!state.settings.printerProfileStatuses?.[action.payload.id]) return;
      delete state.settings.printerProfileStatuses[action.payload.id];
    },
  },
});

// Memoized Settings Selectors
export const selectSettings = (state: RootState) => state.settings.settings;

// Legacy selector kept for compatibility
export const SettingsDetails = selectSettings;

// Derived selectors for specific settings
export const selectPartialOrdersEnabled = (state: RootState) =>
  state.settings.settings.allow_partial_orders;

export const selectStaffOrderAssignmentEnabled = (state: RootState) =>
  state.settings.settings.allow_staff_order_assignment;

export const selectPushNotificationsEnabled = (state: RootState) =>
  state.settings.settings.allow_push_notification;

export const selectTooltipsDisabled = (state: RootState) =>
  state.settings.settings.disable_tooltips;

export const selectShowLastProductRemovalPopup = (state: RootState) =>
  state.settings.settings.showLastProductRemovalPopup;

export const selectShowIncompleteOrderPopup = (state: RootState) =>
  state.settings.settings.showIncompleteOrderPopup;

export const selectSecuritySettings = (state: RootState) =>
  state.settings.settings.security;

export const selectBackofficeAccessMode = (state: RootState) =>
  state.settings?.settings?.security?.backofficeAccessMode ?? 'none';

export const selectEmployeeRequireBiometricsOnForeground = (state: RootState) =>
  state.settings?.settings?.security?.employeeRequireBiometricsOnForeground ??
  false;

export const selectBackofficeUnlockTtlMs = (state: RootState) =>
  state.settings?.settings?.security?.backofficeUnlockTtlMs ??
  2 * 60 * 60 * 1000;

export const selectAllowDeviceCredentialsFallback = (state: RootState) =>
  state.settings?.settings?.security?.allowDeviceCredentialsFallback ?? true;

export const selectPrinterSettings = (state: RootState) =>
  state.settings.settings.printer;

const EMPTY_PROFILES: PrinterProfile[] = [];
const EMPTY_STATUSES: PrinterProfileStatusMap = {};

export const selectPrinterProfiles = (state: RootState): PrinterProfile[] =>
  state.settings.settings.printerProfiles ?? EMPTY_PROFILES;

export const selectPrinterProfileStatuses = (
  state: RootState,
): PrinterProfileStatusMap =>
  state.settings.settings.printerProfileStatuses ?? EMPTY_STATUSES;

export const selectEnabledPrinterProfiles = createSelector(
  selectPrinterProfiles,
  profiles => profiles.filter(p => p.enabled),
);

export const selectShowProductImages = (state: RootState) =>
  state.settings.settings.showProductImages;

/**
 * Alpha rail (A–Z) user preference. Treats missing/undefined as ON so
 * existing persisted users keep the shipped behavior after upgrade.
 * Explicit `false` wins.
 */
export const selectShowAlphaRail = (state: RootState) =>
  state.settings.settings.showAlphaRail !== false;

export const selectEnableTableRotation = (state: RootState) =>
  state.settings.settings.enableTableRotation !== false;

export const selectEnableQuickSell = (state: RootState) =>
  state.settings.settings.enableQuickSell !== false;

export const selectQuickActionOrder = (state: RootState) =>
  state.settings.settings.quickActionOrder;

export const selectEnabledQuickActions = (state: RootState) =>
  state.settings.settings.enabledQuickActions;

// Product card selectors
export const selectProductCard = (state: RootState) =>
  state.settings.settings.productCard ?? DEFAULT_PRODUCT_CARD;

export const selectProductCardLayout = (state: RootState) =>
  selectProductCard(state).layout;

export const selectIsRowLayoutSetting = (state: RootState) =>
  selectProductCardLayout(state) === 'row';

export const selectQuantityButtons = (state: RootState) =>
  selectProductCard(state).quantityButtons ?? DEFAULT_QUANTITY_BUTTONS;

export const selectPlusButtonColors = (state: RootState) =>
  selectQuantityButtons(state).plus;

export const selectMinusButtonColors = (state: RootState) =>
  selectQuantityButtons(state).minus;

// Quantity interaction mode (buttons vs gesture)
export const selectQuantityInteractionMode = (state: RootState) =>
  (selectProductCard(state).quantityInteraction as 'buttons' | 'gesture') ??
  'buttons';

export const selectUseGestureQuantity = (state: RootState) =>
  selectQuantityInteractionMode(state) === 'gesture';

// Icon style selector
export const selectIconStyle = (state: RootState): IconStyleVariant =>
  state.settings.settings.iconStyle ?? 'tinted';

export type QuickActionColorMode = 'monochrome' | 'colored';

export const selectQuickActionColorMode = (
  state: RootState,
): QuickActionColorMode =>
  state.settings.settings.quickActionColorMode ?? 'colored';

export const selectTabletPortraitNavLayout = (
  state: RootState,
): TabletPortraitNavLayout =>
  state.settings.settings.tabletPortraitNavLayout ?? 'native';

export const selectShowTodayStatsValues = (state: RootState): boolean =>
  state.settings.settings.showTodayStatsValues !== false;

export const selectServiceDisplaySortOrder = (
  state: RootState,
): 'desc' | 'asc' => state.settings.settings.serviceDisplaySortOrder ?? 'desc';

export const selectQuickActionsCollapsed = (state: RootState): boolean =>
  state.settings.settings.quickActionsCollapsed === true;

export const {
  addSettings,
  addSingleSetting,
  updateSecuritySettings,
  addPrinterProfiles,
  updatePrinterProfile,
  deletePrinterProfile,
  togglePrinterProfile,
  reorderPrinterProfiles,
  setPrinterProfileStatus,
  clearPrinterProfileStatus,
} = settingsSlice.actions;
export default settingsSlice.reducer;
